import { apiClient } from "./client";
import { canonicalLoomDefinitionJson } from "../features/parametric-geometry/loomAcceptedDefinitionBinding";
import type { LoomAuthoringDocument, LoomPrimitiveDefinition } from "../features/parametric-geometry/loomAuthoringModel";
import { loomAuthoringDocumentStateId } from "../features/parametric-geometry/loomAuthoringHistory";
import {
  LOOM_CONTROL_SURFACE_EVALUATOR_ID,
  LOOM_CONTROL_SURFACE_TOPOLOGY_ID,
  type LoomSha256Revision,
  type LoomWingControlSurfaceCollection,
} from "../features/parametric-geometry/loomWingControlSurfaceContract";

type WingDefinition = Extract<LoomPrimitiveDefinition, { primitive: "wing" }>;
type Side = "port" | "starboard";

export interface LoomControlSurfaceAcceptedBody {
  bodyId: string;
  role: "fixed-wing" | "movable-control";
  controlSurfaceId: string | null;
  side: Side | null;
  positions: Float32Array;
  indices: Uint32Array;
  geometryRevision: LoomSha256Revision;
  componentCount: number;
}

export interface LoomControlSurfaceAcceptedHinge {
  hingeId: string;
  controlSurfaceId: string;
  side: Side;
  startM: readonly [number, number, number];
  endM: readonly [number, number, number];
  maximumDeviationM: number;
  toleranceM: number;
}

export interface LoomControlSurfaceAcceptedHealth {
  controlSurfaceId: string;
  status: "ok" | "sealed" | "collision" | "unavailable";
  minimumGapM: number;
  endGapM: number;
  collides: boolean;
  worstSide: Side | null;
  worstDeflectionDeg: number | null;
  envelopeSamples: number;
  healthId: "sampled-concentric-section-envelope/v1";
  errorCode: string | null;
}

export interface LoomControlSurfaceAcceptedGeometry {
  schema: "loom.control-surface-rebuild-result/v1";
  sourceAuthority: "ephemeral-local-fixture-self-attested/v1";
  requestId: string;
  sourceDefinition: { documentId: string; revision: number; stateId: string };
  definitionRevision: LoomSha256Revision;
  wingDefinitionRevision: LoomSha256Revision;
  wingGeometryRevision: LoomSha256Revision;
  topologyCacheKey: LoomSha256Revision;
  topologyRevision: LoomSha256Revision;
  evaluatorConfigurationRevision: LoomSha256Revision;
  fidelity: {
    sourceGeometry: "adapted-sampled-section-loft";
    noseCove: "highlift-conformance-sampled-concentric";
    kinematics: "exact-rigid-straight-hinge";
    bodyOwnership: "separate-named-bodies";
    fixedTopology: "segmented-capped-multishell";
    spanReference: "clean-wing-leading-edge-arclength/v1";
    openvspExact: false;
    definitionPoseSeparation: true;
  };
  bodies: readonly LoomControlSurfaceAcceptedBody[];
  hinges: readonly LoomControlSurfaceAcceptedHinge[];
  health: readonly LoomControlSurfaceAcceptedHealth[];
  responseRevision: LoomSha256Revision;
}

type JsonRecord = Record<string, unknown>;
const SHA = /^sha256:[0-9a-f]{64}$/;
const TOKEN = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,159}$/;

function record(value: unknown, path: string): JsonRecord {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error(`${path} must be an object`);
  return value as JsonRecord;
}
function exact(value: JsonRecord, fields: readonly string[], path: string): void {
  if (Object.keys(value).length !== fields.length || fields.some((field) => !(field in value))) {
    throw new Error(`${path} has an unsupported field set`);
  }
}
function string(value: unknown, path: string): string {
  if (typeof value !== "string" || !value) throw new Error(`${path} must be a non-empty string`);
  return value;
}
function token(value: unknown, path: string): string {
  const result = string(value, path);
  if (!TOKEN.test(result)) throw new Error(`${path} must be a bounded identifier`);
  return result;
}
function revision(value: unknown, path: string): LoomSha256Revision {
  const result = string(value, path);
  if (!SHA.test(result)) throw new Error(`${path} must be a lowercase SHA-256 revision`);
  return result as LoomSha256Revision;
}
function number(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) throw new Error(`${path} must be finite`);
  return value;
}
function nonnegative(value: unknown, path: string): number {
  const result = number(value, path);
  if (result < 0) throw new Error(`${path} must be nonnegative`);
  return result;
}
function integer(value: unknown, path: string, maximum = Number.MAX_SAFE_INTEGER): number {
  const result = number(value, path);
  if (!Number.isInteger(result) || result < 0 || result > maximum) throw new Error(`${path} is out of bounds`);
  return result;
}
function literal<T extends string>(value: unknown, choices: readonly T[], path: string): T {
  if (typeof value !== "string" || !choices.includes(value as T)) throw new Error(`${path} is unsupported`);
  return value as T;
}
function vector(value: unknown, path: string): readonly [number, number, number] {
  if (!Array.isArray(value) || value.length !== 3) throw new Error(`${path} must be a 3D point`);
  return [number(value[0], `${path}[0]`), number(value[1], `${path}[1]`), number(value[2], `${path}[2]`)];
}
function numericArray(value: unknown, path: string, multiple: number, maximum: number): number[] {
  if (!Array.isArray(value) || value.length === 0 || value.length > maximum || value.length % multiple !== 0) {
    throw new Error(`${path} has an invalid extent`);
  }
  return value.map((entry, index) => number(entry, `${path}[${index}]`));
}

function parseBody(value: unknown, path: string): LoomControlSurfaceAcceptedBody {
  const row = record(value, path);
  exact(row, ["bodyId", "role", "controlSurfaceId", "side", "positions", "indices", "geometryRevision", "componentCount"], path);
  const role = literal(row.role, ["fixed-wing", "movable-control"] as const, `${path}.role`);
  const controlSurfaceId = row.controlSurfaceId === null ? null : string(row.controlSurfaceId, `${path}.controlSurfaceId`);
  const side = row.side === null ? null : literal(row.side, ["port", "starboard"] as const, `${path}.side`);
  if ((role === "fixed-wing") !== (controlSurfaceId === null && side === null)) throw new Error(`${path} ownership is inconsistent`);
  const positions = numericArray(row.positions, `${path}.positions`, 3, 2_000_000);
  const indices = numericArray(row.indices, `${path}.indices`, 3, 4_000_000);
  if (indices.some((index) => !Number.isInteger(index) || index < 0 || index >= positions.length / 3)) {
    throw new Error(`${path}.indices reference unavailable vertices`);
  }
  return { bodyId: string(row.bodyId, `${path}.bodyId`), role, controlSurfaceId, side,
    positions: new Float32Array(positions), indices: new Uint32Array(indices),
    geometryRevision: revision(row.geometryRevision, `${path}.geometryRevision`),
    componentCount: integer(row.componentCount, `${path}.componentCount`, 64) };
}

function parseHinge(value: unknown, path: string): LoomControlSurfaceAcceptedHinge {
  const row = record(value, path);
  exact(row, ["hingeId", "controlSurfaceId", "side", "startM", "endM", "maximumDeviationM", "toleranceM"], path);
  return { hingeId: string(row.hingeId, `${path}.hingeId`), controlSurfaceId: string(row.controlSurfaceId, `${path}.controlSurfaceId`),
    side: literal(row.side, ["port", "starboard"] as const, `${path}.side`),
    startM: vector(row.startM, `${path}.startM`), endM: vector(row.endM, `${path}.endM`),
    maximumDeviationM: number(row.maximumDeviationM, `${path}.maximumDeviationM`),
    toleranceM: number(row.toleranceM, `${path}.toleranceM`) };
}

function parseHealth(value: unknown, path: string): LoomControlSurfaceAcceptedHealth {
  const row = record(value, path);
  exact(row, ["controlSurfaceId", "status", "minimumGapM", "endGapM", "collides", "worstSide", "worstDeflectionDeg", "envelopeSamples", "healthId", "errorCode"], path);
  if (typeof row.collides !== "boolean") throw new Error(`${path}.collides must be boolean`);
  return { controlSurfaceId: string(row.controlSurfaceId, `${path}.controlSurfaceId`),
    status: literal(row.status, ["ok", "sealed", "collision", "unavailable"] as const, `${path}.status`),
    minimumGapM: nonnegative(row.minimumGapM, `${path}.minimumGapM`),
    endGapM: nonnegative(row.endGapM, `${path}.endGapM`),
    collides: row.collides, worstSide: row.worstSide === null ? null : literal(row.worstSide, ["port", "starboard"] as const, `${path}.worstSide`),
    worstDeflectionDeg: row.worstDeflectionDeg === null ? null : number(row.worstDeflectionDeg, `${path}.worstDeflectionDeg`),
    envelopeSamples: integer(row.envelopeSamples, `${path}.envelopeSamples`, 100_000),
    healthId: literal(row.healthId, ["sampled-concentric-section-envelope/v1"] as const, `${path}.healthId`),
    errorCode: row.errorCode === null ? null : string(row.errorCode, `${path}.errorCode`) };
}

async function digest(value: unknown): Promise<LoomSha256Revision> {
  if (!globalThis.crypto?.subtle) throw new Error("WebCrypto SHA-256 is required to verify control geometry");
  const bytes = new TextEncoder().encode(canonicalLoomDefinitionJson(value));
  const hash = await crypto.subtle.digest("SHA-256", bytes);
  return `sha256:${[...new Uint8Array(hash)].map((byte) => byte.toString(16).padStart(2, "0")).join("")}`;
}

export async function parseLoomControlSurfaceAcceptedGeometry(value: unknown): Promise<LoomControlSurfaceAcceptedGeometry> {
  const row = record(value, "controlGeometry");
  exact(row, ["schema", "sourceAuthority", "requestId", "sourceDefinition", "definitionRevision", "wingDefinitionRevision", "wingGeometryRevision", "topologyCacheKey", "topologyRevision", "evaluatorConfigurationRevision", "fidelity", "bodies", "hinges", "health", "responseRevision"], "controlGeometry");
  if (row.schema !== "loom.control-surface-rebuild-result/v1") throw new Error("controlGeometry.schema is unsupported");
  if (row.sourceAuthority !== "ephemeral-local-fixture-self-attested/v1") {
    throw new Error("controlGeometry.sourceAuthority is unsupported");
  }
  const source = record(row.sourceDefinition, "controlGeometry.sourceDefinition");
  exact(source, ["documentId", "revision", "stateId"], "controlGeometry.sourceDefinition");
  const fidelity = record(row.fidelity, "controlGeometry.fidelity");
  exact(fidelity, ["sourceGeometry", "noseCove", "kinematics", "bodyOwnership", "fixedTopology", "spanReference", "openvspExact", "definitionPoseSeparation"], "controlGeometry.fidelity");
  if (fidelity.sourceGeometry !== "adapted-sampled-section-loft" || fidelity.noseCove !== "highlift-conformance-sampled-concentric"
      || fidelity.kinematics !== "exact-rigid-straight-hinge" || fidelity.bodyOwnership !== "separate-named-bodies"
      || fidelity.fixedTopology !== "segmented-capped-multishell" || fidelity.spanReference !== "clean-wing-leading-edge-arclength/v1"
      || fidelity.openvspExact !== false || fidelity.definitionPoseSeparation !== true) throw new Error("controlGeometry fidelity is unsupported");
  if (!Array.isArray(row.bodies) || !Array.isArray(row.hinges) || !Array.isArray(row.health)) throw new Error("controlGeometry collections are invalid");
  const unsigned = { ...row }; delete unsigned.responseRevision;
  const responseRevision = revision(row.responseRevision, "controlGeometry.responseRevision");
  if (await digest(unsigned) !== responseRevision) throw new Error("controlGeometry response revision does not match");
  const bodies = row.bodies.map((entry, index) => parseBody(entry, `controlGeometry.bodies[${index}]`));
  if (bodies.filter(({ role }) => role === "fixed-wing").length !== 1 || bodies.length < 3) throw new Error("controlGeometry must contain one fixed wing and movable bodies");
  return { schema: "loom.control-surface-rebuild-result/v1",
    sourceAuthority: "ephemeral-local-fixture-self-attested/v1",
    requestId: string(row.requestId, "controlGeometry.requestId"),
    sourceDefinition: { documentId: token(source.documentId, "controlGeometry.sourceDefinition.documentId"), revision: integer(source.revision, "controlGeometry.sourceDefinition.revision"), stateId: token(source.stateId, "controlGeometry.sourceDefinition.stateId") },
    definitionRevision: revision(row.definitionRevision, "controlGeometry.definitionRevision"), wingDefinitionRevision: revision(row.wingDefinitionRevision, "controlGeometry.wingDefinitionRevision"),
    wingGeometryRevision: revision(row.wingGeometryRevision, "controlGeometry.wingGeometryRevision"), topologyCacheKey: revision(row.topologyCacheKey, "controlGeometry.topologyCacheKey"), topologyRevision: revision(row.topologyRevision, "controlGeometry.topologyRevision"), evaluatorConfigurationRevision: revision(row.evaluatorConfigurationRevision, "controlGeometry.evaluatorConfigurationRevision"),
    fidelity: fidelity as LoomControlSurfaceAcceptedGeometry["fidelity"], bodies,
    hinges: row.hinges.map((entry, index) => parseHinge(entry, `controlGeometry.hinges[${index}]`)), health: row.health.map((entry, index) => parseHealth(entry, `controlGeometry.health[${index}]`)), responseRevision };
}

export async function rebuildLoomControlSurfaceGeometry(input: {
  projectId: string; document: LoomAuthoringDocument; wing: WingDefinition;
  collection: LoomWingControlSurfaceCollection; definitionRevision: LoomSha256Revision;
  signal?: AbortSignal;
}): Promise<LoomControlSurfaceAcceptedGeometry> {
  const { controlSurfaces: _controls, ...neutralWingDefinition } = input.wing;
  const request = { schema: "loom.control-surface-rebuild-request/v1", requestId: crypto.randomUUID(),
    sourceDefinition: { documentId: input.document.documentId, revision: input.document.revision, stateId: loomAuthoringDocumentStateId(input.document) },
    evaluatorId: LOOM_CONTROL_SURFACE_EVALUATOR_ID, topology: LOOM_CONTROL_SURFACE_TOPOLOGY_ID,
    neutralWingDefinition, controlSurfaceCollection: input.collection,
    settings: { spanSamples: 11, contourSamples: 81 } };
  const result = await apiClient(`/thread/v1/projects/${encodeURIComponent(input.projectId)}/aero/loom/control-surface-rebuilds`, {
    method: "POST", body: request, signal: input.signal,
  });
  const parsed = await parseLoomControlSurfaceAcceptedGeometry(result);
  if (parsed.requestId !== request.requestId || parsed.sourceDefinition.stateId !== request.sourceDefinition.stateId
      || parsed.definitionRevision !== input.definitionRevision || parsed.wingDefinitionRevision !== input.collection.wingDefinitionRevision) {
    throw new Error("control geometry response is stale for the requested definition");
  }
  const expectedBodyIds = new Set([`${input.collection.wingId}:fixed`, ...input.collection.controlSurfaces.flatMap(({ id }) => (
    [`${input.collection.wingId}:control:${id}:port`, `${input.collection.wingId}:control:${id}:starboard`]
  ))]);
  const expectedOwners = new Set(input.collection.controlSurfaces.flatMap(({ id }) => (
    [`${id}\0port`, `${id}\0starboard`]
  )));
  const actualBodyIds = new Set(parsed.bodies.map(({ bodyId }) => bodyId));
  const actualOwners = new Set(parsed.bodies.filter(({ role }) => role === "movable-control")
    .map(({ controlSurfaceId, side }) => `${controlSurfaceId}\0${side}`));
  const hingeOwners = new Set(parsed.hinges.map(({ controlSurfaceId, side }) => `${controlSurfaceId}\0${side}`));
  const healthOwners = new Set(parsed.health.map(({ controlSurfaceId }) => controlSurfaceId));
  const requestedControls = new Set(input.collection.controlSurfaces.map(({ id }) => id));
  const same = <T,>(left: Set<T>, right: Set<T>) => left.size === right.size
    && [...left].every((entry) => right.has(entry));
  if (!same(actualBodyIds, expectedBodyIds) || !same(actualOwners, expectedOwners)
      || !same(hingeOwners, expectedOwners) || !same(healthOwners, requestedControls)) {
    throw new Error("control geometry ownership does not match the requested control collection");
  }
  return parsed;
}
